import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { LocationsGate } from './components';

// Eager load dashboard for instant access
import LocationsDashboard from './dashboard';

// Lazy load all other pages
const CampusesPage = lazy(() => import('./campuses'));
const LocationFormPage = lazy(() => import('./campuses/form'));
const LocationDetailPage = lazy(() => import('./campuses/detail'));
const VenuesPage = lazy(() => import('./venues'));
const ServiceSchedulePage = lazy(() => import('./service-schedule'));
const LeadersPage = lazy(() => import('./leaders'));
const SocialPlatformsPage = lazy(() => import('./social-media'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Location Management Routes
 *
 * Module access (Admin / LocationsAdmin / "Locations Access", or the Location.View right) is the
 * domain gate; inside, pages and actions gate on capabilities (role OR Location.* right) via
 * LocationsGate and useLocationsPermissions. LocationID 0 ("Online Campus") is a real location.
 */
export default function LocationManagementRoutes() {
  return (
    <MainLayout domainKey="locations">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Dashboard - Default route */}
          <Route path="/" element={<LocationsGate allow={(p) => p.canView}><LocationsDashboard /></LocationsGate>} />

          {/* Campuses */}
          <Route path="/campuses" element={<LocationsGate allow={(p) => p.canView}><CampusesPage /></LocationsGate>} />
          <Route path="/campuses/new" element={<LocationsGate allow={(p) => p.canCreate}><LocationFormPage /></LocationsGate>} />
          <Route path="/campuses/:id/edit" element={<LocationsGate allow={(p) => p.canUpdate}><LocationFormPage /></LocationsGate>} />
          <Route path="/campuses/:id/*" element={<LocationsGate allow={(p) => p.canView}><LocationDetailPage /></LocationsGate>} />

          {/* Across all campuses */}
          <Route path="/venues" element={<LocationsGate allow={(p) => p.canViewVenues}><VenuesPage /></LocationsGate>} />
          <Route path="/service-schedule" element={<LocationsGate allow={(p) => p.canViewServiceTimes}><ServiceSchedulePage /></LocationsGate>} />
          <Route path="/leaders" element={<LocationsGate allow={(p) => p.canViewLeaders}><LeadersPage /></LocationsGate>} />

          {/* Settings */}
          <Route path="/social-media" element={<LocationsGate allow={(p) => p.canViewPlatforms}><SocialPlatformsPage /></LocationsGate>} />

          {/* Old menu links (Buildings / Regions were never built) */}
          <Route path="/buildings" element={<Navigate to="/app/locations/venues" replace />} />
          <Route path="/regions" element={<Navigate to="/app/locations/campuses" replace />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/locations" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
